import { HygienistProps } from './types';
import { RateSelectorConfigResponse } from '../api/Api';
import { DEFAULT_RATE_SELECTOR_CONFIG, getRateSelectorConfigSync } from '../hooks/useRateSelectorConfig';

// ============================================
// TYPES
// ============================================

export interface RateRangeResult {
    bottomRate: number;
    topRate: number;
    proposedRate: number;
}

export interface ColorThresholds {
    greenMax: number;      // Up to this value is green
    yellowMax: number;     // Up to this value is yellow (after green)
    // Above yellowMax: bottom 2 values are orange, rest are red
}

export type RateColor = 'green' | 'yellow' | 'orange' | 'red';

export interface RateOption {
    rate: number;
    color: RateColor;
    isRecommended: boolean;
    isHighForShift: boolean;  // true for orange only
    isTooHigh: boolean;       // true for red only
}

// Re-export the config type for convenience
export type { RateSelectorConfigResponse };

// ============================================
// CONFIG ACCESSOR
// ============================================

/**
 * Get the current config, either passed explicitly or from cache/defaults.
 * This allows functions to work both with and without explicit config.
 */
function getConfig(config?: RateSelectorConfigResponse): RateSelectorConfigResponse {
    return config ?? getRateSelectorConfigSync();
}

// Minimum valid graduation year for data validation
const MIN_VALID_GRADUATION_YEAR = 1970;

// ============================================
// VALIDATION
// ============================================

/**
 * Validates that graduationYear is a valid integer greater than 1970
 * @returns true if valid, false if invalid (should show error modal)
 */
export const isValidGraduationYear = (graduationYear: number | undefined): boolean => {
    if (graduationYear === undefined || graduationYear === null) return false;
    if (!Number.isInteger(graduationYear)) return false;
    return graduationYear > MIN_VALID_GRADUATION_YEAR;
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Determines if talent is a "new grad" (graduated within threshold years)
 */
export const isNewGrad = (
    graduationYear: number | undefined, 
    config?: RateSelectorConfigResponse
): boolean => {
    if (!graduationYear || graduationYear <= MIN_VALID_GRADUATION_YEAR) return false;
    const currentYear = new Date().getFullYear();
    const cfg = getConfig(config);
    return (currentYear - graduationYear) < cfg.newGradThresholdYears;
};

/**
 * Gets the graduation year adjustment for rate calculation
 * Returns the configured adjustment for new grads, 0 otherwise
 */
export const getGraduationYearAdjustment = (
    graduationYear: number | undefined,
    config?: RateSelectorConfigResponse
): number => {
    const cfg = getConfig(config);
    return isNewGrad(graduationYear, cfg) ? cfg.newGradAdjustment : 0;
};

/**
 * Gets the status adjustment for rate calculation
 * Returns the configured adjustment based on status
 */
export const getStatusAdjustment = (
    status: number | undefined,
    config?: RateSelectorConfigResponse
): number => {
    const cfg = getConfig(config);
    if (status === undefined || status < 1) return cfg.statusAdjustments[0] ?? 0;
    return cfg.statusAdjustments[status] ?? cfg.statusAdjustments[2] ?? 10; // Default to Elite for any status > 2
};

/**
 * Gets color thresholds based on talent status and new grad status.
 * For new grads with status < 1, applies compression to ensure all color bands fit.
 */
export const getColorThresholds = (
    status: number | undefined,
    graduationYear?: number | undefined,
    config?: RateSelectorConfigResponse
): { greenMax: number; yellowMax: number } => {
    const cfg = getConfig(config);
    
    // Determine base thresholds by status
    let baseThresholds: { greenMax: number; yellowMax: number };
    
    if (status === undefined || status < 1) {
        baseThresholds = cfg.colorThresholds[0] ?? { greenMax: 1.10, yellowMax: 1.20 };
    } else if (status === 1) {
        baseThresholds = cfg.colorThresholds[1] ?? { greenMax: 1.125, yellowMax: 1.22 };
    } else {
        // status >= 2 uses Elite thresholds
        baseThresholds = cfg.colorThresholds[2] ?? { greenMax: 1.15, yellowMax: 1.275 };
    }
    
    // Apply compression for new grads with status < 1
    // This ensures all color bands (green, yellow, orange, red) remain visible
    // within the reduced rate range
    if (isNewGrad(graduationYear, cfg) && (status === undefined || status < 1)) {
        const greenExcess = baseThresholds.greenMax - 1.0;
        const yellowExcess = baseThresholds.yellowMax - 1.0;
        
        return {
            greenMax: 1.0 + (greenExcess * cfg.newGradColorCompression),
            yellowMax: 1.0 + (yellowExcess * cfg.newGradColorCompression),
        };
    }
    
    return baseThresholds;
};

// ============================================
// MAIN CALCULATION FUNCTIONS
// ============================================

/**
 * Calculates the personalized rate range for a talent viewing a job
 * 
 * @param proposedRate - The job's proposed rate (from dental office, always integer)
 * @param graduationYear - Talent's graduation year
 * @param status - Talent's status (0=Probation, 1=Pro, 2=Elite)
 * @param config - Optional rate selector config (uses cached/default if not provided)
 * @returns Object with bottomRate, topRate, and original proposedRate
 */
export const calculateRateRange = (
    proposedRate: number,
    graduationYear: number | undefined,
    status: number | undefined,
    config?: RateSelectorConfigResponse,
    minimumTopRate?: number
): RateRangeResult => {
    if (!proposedRate || proposedRate <= 0) {
        return { bottomRate: 0, topRate: 0, proposedRate: 0 };
    }

    const cfg = getConfig(config);

    // Bottom rate: proposedRate * bottomRateMultiplier (same for everyone)
    const bottomRate = Math.round(proposedRate * cfg.bottomRateMultiplier);

    // Base top rate: proposedRate * baseTopRateMultiplier
    const baseTopRate = Math.round(proposedRate * cfg.baseTopRateMultiplier);

    // Adjustments
    const gradAdjustment = getGraduationYearAdjustment(graduationYear, cfg);
    const statusAdjustment = getStatusAdjustment(status, cfg);

    // Final top rate with adjustments, capped by maxDeltaCap
    const maxDelta = cfg.maxDeltaCap ?? 12;
    const uncappedTopRate = baseTopRate + gradAdjustment + statusAdjustment;
    const cappedTopRate = Math.min(uncappedTopRate, proposedRate + maxDelta);
    const normalizedMinimumTopRate = Number.isFinite(Number(minimumTopRate))
        ? Math.round(Number(minimumTopRate))
        : 0;
    const topRate = Math.max(cappedTopRate, normalizedMinimumTopRate);

    return {
        bottomRate: Math.max(1, bottomRate), // Ensure minimum of $1
        topRate: Math.max(bottomRate, topRate), // Ensure top >= bottom
        proposedRate,
    };
};

/**
 * Calculates the maximum "yellow" rate for filtering purposes.
 * Shifts should be shown if their yellow range (or below) falls within the filter.
 * 
 * @param proposedRate - The job's proposed rate
 * @param status - Talent's status
 * @param config - Optional rate selector config
 * @returns The maximum rate that is still in the yellow zone
 */
export const getYellowMaxRate = (
    proposedRate: number,
    status: number | undefined,
    config?: RateSelectorConfigResponse
): number => {
    const thresholds = getColorThresholds(status, undefined, config);
    return Math.round(proposedRate * thresholds.yellowMax);
};

/**
 * Determines the color for a specific rate value based on talent status.
 * 
 * Color assignment (with graceful rounding - on boundary, use lower severity):
 * - Green: bottomRate to greenMax (inclusive)
 * - Yellow: greenMax+1 to yellowMax (inclusive)
 * - Orange: First N values after yellowMax (transition zone, configurable)
 * - Red: Everything above orange
 * 
 * @param rate - The hourly rate to evaluate
 * @param proposedRate - The job's proposed rate
 * @param status - Talent's status
 * @param topRate - The calculated top rate (needed for orange calculation)
 * @param graduationYear - Talent's graduation year (for new grad compression)
 * @param config - Optional rate selector config
 * @returns 'green', 'yellow', 'orange', or 'red'
 */
export const getRateColor = (
    rate: number,
    proposedRate: number,
    status: number | undefined,
    topRate: number,
    graduationYear?: number | undefined,
    config?: RateSelectorConfigResponse
): RateColor => {
    const cfg = getConfig(config);
    const thresholds = getColorThresholds(status, graduationYear, cfg);
    
    // Calculate threshold values
    const greenMaxRate = Math.round(proposedRate * thresholds.greenMax);
    const yellowMaxRate = Math.round(proposedRate * thresholds.yellowMax);

    // Green zone: from bottom up to greenMax (inclusive)
    if (rate <= greenMaxRate) {
        return 'green';
    }
    
    // Yellow zone: from greenMax+1 up to yellowMax (inclusive)
    if (rate <= yellowMaxRate) {
        return 'yellow';
    }
    
    // After yellow comes orange (first N values) then red
    // Calculate the first rate after yellow
    const firstAfterYellow = yellowMaxRate + 1;
    
    // Orange zone: first N values after yellow (if they exist within range)
    const orangeEndRate = firstAfterYellow + cfg.orangeCount - 1;
    
    if (rate <= orangeEndRate && rate <= topRate) {
        return 'orange';
    }
    
    // Red zone: everything else
    return 'red';
};

/**
 * Converts rate color to CSS color value using design system variables
 */
export const colorToCssValue = (color: RateColor): string => {
    switch (color) {
        case 'green':
            return 'var(--success-main, #13CA82)';
        case 'yellow':
            return '#F5A623';
        case 'orange':
            return '#FB8C00';
        case 'red':
            return 'var(--primary-main, #FB7672)';
        default:
            return 'inherit';
    }
};

/**
 * Generates the full list of rate options with colors and labels.
 * 
 * Guarantees:
 * - Continuous +1 integer incrementation from bottom to top
 * - No duplicate values
 * - Every value has a color (no "colorless" values)
 * 
 * @param proposedRate - The job's proposed rate
 * @param talent - The talent object with graduationYear and status
 * @param config - Optional rate selector config
 * @returns Array of rate options with color and label information
 */
export const generateRateOptions = (
    proposedRate: number,
    talent: Pick<HygienistProps, 'graduationYear' | 'status'>,
    config?: RateSelectorConfigResponse,
    minimumTopRate?: number
): RateOption[] => {
    const cfg = getConfig(config);
    const range = calculateRateRange(proposedRate, talent.graduationYear, talent.status, cfg, minimumTopRate);
    
    if (range.bottomRate === 0 || range.topRate === 0) {
        return [];
    }

    const options: RateOption[] = [];
    
    // Generate continuous integer values from bottom to top
    for (let rate = range.bottomRate; rate <= range.topRate; rate++) {
        const color = getRateColor(rate, proposedRate, talent.status, range.topRate, talent.graduationYear, cfg);
        options.push({
            rate,
            color,
            isRecommended: rate === proposedRate,
            isHighForShift: color === 'orange',
            isTooHigh: color === 'red',
        });
    }

    return options;
};

/**
 * Formats the rate range as a display string for job cards
 * 
 * @param proposedRate - The job's proposed rate
 * @param talent - The talent object
 * @param config - Optional rate selector config
 * @returns Formatted string like "$45/hr - $65/hr"
 */
export const formatRateRangeDisplay = (
    proposedRate: number,
    talent: Pick<HygienistProps, 'graduationYear' | 'status'>,
    config?: RateSelectorConfigResponse
): string => {
    const range = calculateRateRange(proposedRate, talent.graduationYear, talent.status, config);
    
    if (range.bottomRate === 0 || range.topRate === 0) {
        return '$0/hr - $0/hr';
    }

    return `$${range.bottomRate}/hr - $${range.topRate}/hr`;
};

/**
 * Gets the default (starting) rate for the selector.
 * The selector should open at the office proposed rate.
 */
export const getDefaultRate = (
    proposedRate: number,
    talent: Pick<HygienistProps, 'graduationYear' | 'status'>,
    config?: RateSelectorConfigResponse,
    minimumTopRate?: number
): number => {
    const range = calculateRateRange(proposedRate, talent.graduationYear, talent.status, config, minimumTopRate);
    return range.proposedRate || proposedRate;
};

// ============================================
// UNLOCK INFO (Pro/Elite status display)
// ============================================

export interface RateUnlockInfo {
    statusLabel: string;
    topRate: number;
    baseTopRate: number;
    unlockedAmount: number;
    hasUnlock: boolean;
}

const STATUS_LABELS: Record<number, string> = { 0: 'Active', 1: 'Pro', 2: 'Elite' };

/**
 * Returns unlock info for Pro/Elite display in the Offer Modal.
 * Compares the talent's personalized top rate against what an Active-status member would get.
 */
export const getRateUnlockInfo = (
    proposedRate: number,
    talent: Pick<HygienistProps, 'graduationYear' | 'status'>,
    config?: RateSelectorConfigResponse,
    minimumTopRate?: number
): RateUnlockInfo => {
    const cfg = getConfig(config);
    const range = calculateRateRange(proposedRate, talent.graduationYear, talent.status, cfg, minimumTopRate);
    const activeRange = calculateRateRange(proposedRate, talent.graduationYear, 0, cfg, minimumTopRate);
    const status = talent.status ?? 0;
    const unlockedAmount = Math.max(0, range.topRate - activeRange.topRate);

    return {
        statusLabel: STATUS_LABELS[status] ?? 'Active',
        topRate: range.topRate,
        baseTopRate: activeRange.topRate,
        unlockedAmount,
        hasUnlock: status >= 1 && unlockedAmount > 0,
    };
};

// ============================================
// ACCEPTANCE LIKELIHOOD
// ============================================

const ACCEPTANCE_MESSAGES: Record<RateColor, string> = {
    green: 'Great choice \u2014 competitive rate for this shift',
    yellow: 'Good rate \u2014 slightly above the posted range',
    orange: 'Higher rate \u2014 this may reduce your chances of being accepted',
    red: 'This rate is significantly above the shift budget and is unlikely to be accepted',
};

/**
 * Returns an acceptance likelihood message and color for the currently selected rate.
 */
export const getAcceptanceFeedback = (
    selectedRate: number,
    proposedRate: number,
    talent: Pick<HygienistProps, 'graduationYear' | 'status'>,
    config?: RateSelectorConfigResponse,
    minimumTopRate?: number
): { message: string; color: RateColor; cssColor: string } => {
    const cfg = getConfig(config);
    const range = calculateRateRange(proposedRate, talent.graduationYear, talent.status, cfg, minimumTopRate);
    const rateColor = getRateColor(selectedRate, proposedRate, talent.status, range.topRate, talent.graduationYear, cfg);

    return {
        message: ACCEPTANCE_MESSAGES[rateColor],
        color: rateColor,
        cssColor: colorToCssValue(rateColor),
    };
};

// ============================================
// RATE FILTER
// ============================================

/**
 * Determines if a job should be shown based on rate filter.
 * 
 * Filter logic: Show the shift if its yellow range (or below) meets the filter minimum.
 * If ONLY red values would fall in the filter range, do NOT show the shift.
 * 
 * @param proposedRate - The job's proposed rate
 * @param status - Talent's status
 * @param filterMinRate - The minimum rate selected in the filter
 * @param config - Optional rate selector config
 * @returns true if the job should be shown
 */
export const shouldShowJobInRateFilter = (
    proposedRate: number,
    status: number | undefined,
    filterMinRate: number,
    config?: RateSelectorConfigResponse
): boolean => {
    const yellowMax = getYellowMaxRate(proposedRate, status, config);
    // Show job if the yellow max rate meets or exceeds the filter minimum
    return yellowMax >= filterMinRate;
};
